import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";

const WebSocketContext = createContext(null);

const RECONNECT_BASE_DELAY = 1000;
const RECONNECT_MAX_DELAY = 30000;
const PING_INTERVAL_MS = 25000;

export const WebSocketProvider = ({ children }) => {
  const [isConnected, setIsConnected] = useState(false);
  const [unreadCount, setUnreadCount] = useState(() => {
    if (typeof window === "undefined") {
      return 0;
    }
    return Number(localStorage.getItem("rmc2.chat_unread_count") || 0);
  });
  const socketRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const shouldReconnectRef = useRef(true);
  const listenersRef = useRef(new Set());

  const resolveSocketHost = () => {
    if (typeof window === "undefined") {
      return "ecare.rmc2.com.ph";
    }
    const host = window.location.hostname;
    if (host === "dev-ecare.rmc2.com.ph") {
      return host;
    }
    if (host === "ecare.rmc2.com.ph") {
      return host;
    }
    return "ecare.rmc2.com.ph";
  };

  const getWebSocketUrl = () => {
    const token = localStorage.getItem("rmc2.access_token");
    if (!token) {
      return null;
    }
    const protocol = window?.location?.protocol === "http:" ? "ws" : "wss";
    const host = resolveSocketHost();
    return `${protocol}://${host}/ws?token=${token}`;
  };

  const clearReconnectTimer = () => {
    if (reconnectTimerRef.current) {
      clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }
  };

  const clearPingTimer = () => {
    if (pingTimerRef.current) {
      clearInterval(pingTimerRef.current);
      pingTimerRef.current = null;
    }
  };

  const scheduleReconnect = useCallback(() => {
    if (!shouldReconnectRef.current) {
      return;
    }
    clearReconnectTimer();
    const attempt = reconnectAttemptsRef.current + 1;
    reconnectAttemptsRef.current = attempt;
    const delay = Math.min(RECONNECT_BASE_DELAY * 2 ** (attempt - 1), RECONNECT_MAX_DELAY);
    const jitter = Math.floor(Math.random() * 300);
    reconnectTimerRef.current = setTimeout(() => {
      connectSocket();
    }, delay + jitter);
  }, []);

  const connectSocket = useCallback(() => {
    const url = getWebSocketUrl();
    if (!url) {
      return;
    }
    if (socketRef.current?.readyState === WebSocket.OPEN || socketRef.current?.readyState === WebSocket.CONNECTING) {
      return;
    }
    const socket = new WebSocket(url);
    socketRef.current = socket;

    socket.onopen = () => {
      clearReconnectTimer();
      reconnectAttemptsRef.current = 0;
      setIsConnected(true);
      clearPingTimer();
      if (PING_INTERVAL_MS > 0) {
        pingTimerRef.current = setInterval(() => {
          if (socketRef.current?.readyState === WebSocket.OPEN) {
            socketRef.current.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
      }
    };
    socket.onclose = () => {
      setIsConnected(false);
      clearPingTimer();
      scheduleReconnect();
    };
    socket.onerror = () => {
      setIsConnected(false);
      clearPingTimer();
      scheduleReconnect();
    };
    socket.onmessage = (event) => {
      let payload = null;
      try {
        payload = JSON.parse(event.data);
      } catch (error) {
        return;
      }
      if (!payload) {
        return;
      }
      if (payload.type === "chat_unread_badge") {
        const count = Number(payload.unread_count || 0);
        setUnreadCount(count);
        localStorage.setItem("rmc2.chat_unread_count", String(count));
      }
      listenersRef.current.forEach((listener) => {
        listener(payload);
      });
    };
  }, [scheduleReconnect]);

  useEffect(() => {
    shouldReconnectRef.current = true;
    connectSocket();
    return () => {
      shouldReconnectRef.current = false;
      clearReconnectTimer();
      clearPingTimer();
      socketRef.current?.close();
    };
  }, [connectSocket]);

  const addMessageListener = useCallback((listener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const sendMessage = useCallback((message) => {
    if (socketRef.current?.readyState !== WebSocket.OPEN) {
      return false;
    }
    socketRef.current.send(JSON.stringify(message));
    return true;
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        isConnected,
        unreadCount,
        addMessageListener,
        sendMessage,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within WebSocketProvider");
  }
  return context;
};
